import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  FulfillmentWaveOrderStatus,
  WaveAdminDetail,
  WaveAdminOrder,
} from '../../services/fulfillment/types';
import {
  WAVE_ORDER_STATUS_LABELS,
  WAVE_STATUS_COLORS,
  WAVE_STATUS_LABELS,
  WAVE_TYPE_LABELS,
} from '../../services/fulfillment/constants';
import {
  cancelWave,
  completeWave,
  getWaveAdminDetail,
  updateWaveOrderStatus,
  updateWaveStatus,
} from '../../services/fulfillment/waveApi';
import { PageWrapper } from '../../styledComponents/wrappers';
import { FormatDateString } from '../../utils/helper';

const WAVE_ORDER_STATUSES: FulfillmentWaveOrderStatus[] = [
  'pending',
  'picking',
  'picked',
  'cancelled',
];

const WaveDetail: React.FC = () => {
  const { waveId } = useParams<{ waveId: string }>();
  const history = useHistory();
  const [wave, setWave] = useState<WaveAdminDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        setWave(await getWaveAdminDetail(Number(waveId)));
      } catch {
        setError('Failed to load wave.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [waveId]);

  const handleComplete = async () => {
    try {
      await completeWave(Number(waveId));
      setWave(prev => (prev ? { ...prev, status: 'completed' } : prev));
    } catch (err) {
      setActionError(err?.response?.data?.message ?? 'Failed to complete wave');
    }
  };

  const handleCancel = async () => {
    try {
      await cancelWave(Number(waveId));
      setWave(prev => (prev ? { ...prev, status: 'cancelled' } : prev));
    } catch (err) {
      const status = err?.response?.status;
      setActionError(
        status === 403 ? 'Only the assigned picker can cancel this wave' : 'Failed to cancel wave',
      );
    }
  };

  const handleReopen = async () => {
    try {
      await updateWaveStatus(Number(waveId), 'in_progress');
      setWave(prev => (prev ? { ...prev, status: 'in_progress' } : prev));
    } catch {
      setActionError('Failed to reopen wave');
    }
  };

  const handleWaveOrderStatusChange = async (
    waveOrderId: number,
    newStatus: FulfillmentWaveOrderStatus,
  ) => {
    try {
      await updateWaveOrderStatus(waveOrderId, newStatus);
      setWave(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          orders: prev.orders.map(o =>
            o.waveOrderId === waveOrderId ? { ...o, status: newStatus } : o,
          ),
        };
      });
      // refresh to pick up possible wave status change from checkAndCompleteWave
      const updated = await getWaveAdminDetail(Number(waveId));
      setWave(updated);
    } catch {
      setActionError('Failed to update order status');
    }
  };

  const toggleOrder = (waveOrderId: number) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(waveOrderId)) {
        next.delete(waveOrderId);
      } else {
        next.add(waveOrderId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <PageWrapper>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </PageWrapper>
    );
  }

  if (error || !wave) {
    return (
      <PageWrapper>
        <Alert severity="error">{error ?? 'Wave not found.'}</Alert>
      </PageWrapper>
    );
  }

  const { bg, color } = WAVE_STATUS_COLORS[wave.status] ?? { bg: '#eee', color: '#333' };

  const renderOrderRow = (order: WaveAdminOrder) => {
    const expanded = expandedOrders.has(order.waveOrderId);
    return (
      <Box key={order.waveOrderId} sx={{ borderBottom: '1px solid', borderColor: 'grey.200' }}>
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          px={2}
          py={1.5}
          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
          onClick={() => toggleOrder(order.waveOrderId)}
        >
          <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
            {order.shopifyOrderName}
          </Typography>
          <Select
            size="small"
            value={order.status}
            onClick={e => e.stopPropagation()}
            onChange={e =>
              handleWaveOrderStatusChange(
                order.waveOrderId,
                e.target.value as FulfillmentWaveOrderStatus,
              )
            }
            sx={{ fontSize: 12, minWidth: 110 }}
          >
            {WAVE_ORDER_STATUSES.map(s => (
              <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>
                {WAVE_ORDER_STATUS_LABELS[s] ?? s}
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ fontSize: 12, color: 'grey.500', minWidth: 60, textAlign: 'right' }}>
            {order.lineItems.length} item{order.lineItems.length !== 1 ? 's' : ''}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'grey.400' }}>{expanded ? '▲' : '▼'}</Typography>
        </Box>
        <Collapse in={expanded}>
          <Box sx={{ bgcolor: 'grey.50', px: 3, py: 1 }}>
            {order.lineItems.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: 'grey.500', py: 1 }}>
                No line items recorded.
              </Typography>
            ) : (
              order.lineItems.map(li => (
                <Box
                  key={li.shopifyLineItemGid}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  py={0.75}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    '&:last-child': { border: 'none' },
                  }}
                >
                  <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
                    {li.sku ?? '—'}
                  </Typography>
                  {li.lineStatus && (
                    <Chip label={li.lineStatus} size="small" sx={{ fontSize: 11 }} />
                  )}
                  {li.exceptionType && (
                    <Chip
                      label={li.exceptionType}
                      size="small"
                      color="error"
                      sx={{ fontSize: 11 }}
                    />
                  )}
                  {li.fulfillmentBinCode && (
                    <Typography sx={{ fontSize: 12, color: 'grey.600' }}>
                      {li.fulfillmentBinCode}
                    </Typography>
                  )}
                </Box>
              ))
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <PageWrapper>
      <Button
        variant="text"
        size="small"
        onClick={() => history.push('/fulfillment/waves')}
        sx={{ mb: 2 }}
      >
        ← Waves
      </Button>

      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <Typography variant="h5">Wave #{wave.waveId}</Typography>
        <Chip
          label={WAVE_STATUS_LABELS[wave.status]}
          size="small"
          sx={{ bgcolor: bg, color, fontWeight: 600 }}
        />
        <Chip label={WAVE_TYPE_LABELS[wave.type]} size="small" variant="outlined" />
      </Box>

      <Box display="flex" gap={4} mb={3} flexWrap="wrap">
        {wave.assignedToName && (
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Assigned To
            </Typography>
            <Typography sx={{ fontSize: 14 }}>{wave.assignedToName}</Typography>
          </Box>
        )}
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              color: 'grey.500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Created By
          </Typography>
          <Typography sx={{ fontSize: 14 }}>{wave.createdByName}</Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              color: 'grey.500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Created
          </Typography>
          <Typography sx={{ fontSize: 14 }}>{FormatDateString(wave.created)}</Typography>
        </Box>
      </Box>

      {/* Action buttons */}
      <Box display="flex" gap={1} mb={3}>
        {wave.status === 'in_progress' && (
          <>
            <Button variant="contained" color="success" size="small" onClick={handleComplete}>
              Mark Complete
            </Button>
            <Button variant="outlined" color="error" size="small" onClick={handleCancel}>
              Cancel Wave
            </Button>
          </>
        )}
        {(wave.status === 'cancelled' || wave.status === 'completed') && (
          <Button variant="outlined" size="small" onClick={handleReopen}>
            Reopen
          </Button>
        )}
      </Box>

      {/* Orders table */}
      <Typography variant="h6" gutterBottom>
        Orders ({wave.orders.length})
      </Typography>
      <Box
        sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}
      >
        {/* Table header */}
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          px={2}
          py={1}
          sx={{ bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'grey.300' }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: 11,
              fontWeight: 700,
              color: 'grey.500',
              textTransform: 'uppercase',
            }}
          >
            Order
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: 'grey.500',
              textTransform: 'uppercase',
              minWidth: 110,
            }}
          >
            Status
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: 'grey.500',
              textTransform: 'uppercase',
              minWidth: 60,
              textAlign: 'right',
            }}
          >
            Items
          </Typography>
          <Box sx={{ minWidth: 16 }} />
        </Box>
        {wave.orders.length === 0 ? (
          <Box px={2} py={3}>
            <Typography sx={{ fontSize: 13, color: 'grey.500' }}>
              No orders in this wave.
            </Typography>
          </Box>
        ) : (
          wave.orders.map(renderOrderRow)
        )}
      </Box>

      <Snackbar
        open={!!actionError}
        autoHideDuration={5000}
        onClose={() => setActionError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      </Snackbar>
    </PageWrapper>
  );
};

export default WaveDetail;
